import React, { useState } from 'react';
import './FlightTelemetryPane.css'; // Reusing the same styles
// Import the downloaded icons
import rcIconSrc from './icons/rc-icon.svg';
import uavIconSrc from './icons/uav-icon.svg';

// Use imported icons
const DroneIcon = () => <img src={uavIconSrc} alt="UAV" width="24" height="24" />;
const RCIcon = () => <img src={rcIconSrc} alt="RC" width="24" height="24" />;

// Battery icon with level indicator
const BatteryIcon = ({ level }) => {
  const fillColor = 
    level > 60 ? '#049D2F' : 
    level > 20 ? '#FFA500' : 
    '#AA2222';
    
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="5" width="10" height="6" rx="1" stroke="#E4E4E4" strokeWidth="1"/>
      <rect x="12" y="7" width="2" height="2" rx="0.5" stroke="#E4E4E4" strokeWidth="1"/>
      <rect x="3" y="6" width={Math.max(1, level/100 * 8)} height="4" rx="0.5" fill={fillColor}/>
    </svg>
  );
};

// Signal strength icon
const SignalIcon = ({ strength }) => {
  const bars = Math.ceil(strength / 20); // 0-5 bars based on 0-100 strength
  
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 10.5V12" stroke={bars >= 1 ? "#E4E4E4" : "#35373B"} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M6 8.5V12" stroke={bars >= 2 ? "#E4E4E4" : "#35373B"} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M9 6.5V12" stroke={bars >= 3 ? "#E4E4E4" : "#35373B"} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M12 4.5V12" stroke={bars >= 4 ? "#E4E4E4" : "#35373B"} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
};

// Clock icon
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="#E4E4E4" strokeWidth="1"/>
    <path d="M8 5V8H11" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// GPS satellite icon
const SatelliteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 12L6 8" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M10 8L14 4" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <circle cx="8" cy="8" r="2" stroke="#E4E4E4" strokeWidth="1"/>
    <path d="M4.5 4.5L6 6" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M10 10L11.5 11.5" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

// Airspace icon
const AirspaceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12L8 4L12 12H4Z" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.5 8.5H9.5" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M6 10H10" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

// Add a new ToggleVisibilityButton component
const ToggleVisibilityButton = ({ isVisible, onClick }) => (
  <button 
    className="visibility-toggle" 
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    aria-label={isVisible ? "Hide" : "Show"}
  >
    {isVisible ? (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 8L13 8" stroke="#E4E4E4" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 8L13 8" stroke="#E4E4E4" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M8 3L8 13" stroke="#E4E4E4" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    )}
  </button>
);

// Modify the WidgetHeader component to include the visibility toggle
const WidgetHeader = ({ icon, title, subtitle, isConnected, rightElement, onToggleVisibility, isVisible }) => (
  <div className="widget-header">
    <div className="title-container">
      <div className="badged-icon">
        {icon}
        <span className={`badge ${isConnected ? 'connected' : 'disconnected'}`}></span>
      </div>
      <div className="header-content">
        <h3 className="heading">{title}</h3>
        <span className="sub-heading">{subtitle}</span>
      </div>
    </div>
    <div className="header-actions">
      {isConnected ? (
        <div className="tag connected">
          <span>Connected</span>
        </div>
      ) : (
        <div className="tag disconnected">
          <span>Disconnected</span>
        </div>
      )}
      {onToggleVisibility && <ToggleVisibilityButton isVisible={isVisible} onClick={onToggleVisibility} />}
      {rightElement}
    </div>
  </div>
);

// Modify the SimpleHeader component
const SimpleHeader = ({ title, rightElement, onToggleVisibility, isVisible }) => (
  <div className="widget-header simple">
    <div className="title-container">
      <div className="header-content">
        <h3 className="heading">{title}</h3>
      </div>
    </div>
    <div className="header-actions">
      {onToggleVisibility && <ToggleVisibilityButton isVisible={isVisible} onClick={onToggleVisibility} />}
      {rightElement}
    </div>
  </div>
);

// Data block for displaying telemetry values
const DataBlock = ({ icon, label, value, wide }) => (
  <div className={`data-block ${wide ? 'wide' : ''}`}>
    {icon && <div className="data-icon">{icon}</div>}
    <div className="data-content">
      <span className="value">{value}</span>
      <span className="label">{label}</span>
    </div>
  </div>
);

// Toggle switch component
const ToggleSwitch = ({ options, activeIndex, onChange }) => (
  <div className="toggle-switch">
    {options.map((option, index) => (
      <div 
        key={index} 
        className={`toggle-option ${index === activeIndex ? 'active' : ''}`}
        onClick={() => onChange(index)}
      >
        {option}
      </div>
    ))}
  </div>
);

// Compass component
const Compass = ({ heading = 0 }) => {
  // Convert heading to compass direction
  const getDirection = (heading) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'];
    const index = Math.round(heading / 45) % 8;
    return directions[index];
  };
  
  // Format heading for display
  const formatHeading = (heading) => {
    // Ensure heading is a number and within 0-360 range
    let numHeading = Number(heading) || 0;
    numHeading = ((numHeading % 360) + 360) % 360;
    
    // Format to two decimal places
    return numHeading.toFixed(2);
  };
  
  return (
    <div className="compass">
      <div className="compass-rotating" style={{ transform: `rotate(${-heading}deg)` }}>
        <div className="compass-north">N</div>
        <div className="compass-south">S</div>
        <div className="compass-east">E</div>
        <div className="compass-west">W</div>
      </div>
      <div className="compass-fixed">
        <div className="center-dot"></div>
      </div>
      <div className="compass-heading">{formatHeading(heading)}°{getDirection(heading)}</div>
    </div>
  );
};


// Flight mode icon
const FlightModeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V14" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M3 5L13 5" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M3 11L13 11" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M5 8L11 8" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

// Altitude icon
const AltitudeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 13H13" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M8 3L8 10" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M5 6L8 3L11 6" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Speed icon
const SpeedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6" stroke="#E4E4E4" strokeWidth="1"/>
    <path d="M8 8L11 5" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <circle cx="8" cy="8" r="1" stroke="#E4E4E4" strokeWidth="1"/>
  </svg>
);

// Wind icon
const WindIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 8H10C11.1046 8 12 7.10457 12 6C12 4.89543 11.1046 4 10 4" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M2 12H13C14.1046 12 15 11.1046 15 10C15 8.89543 14.1046 8 13 8" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
    <path d="M2 4H6" stroke="#E4E4E4" strokeWidth="1" strokeLinecap="round"/>
  </svg>
);

// RCTelemetry Widget
export const RCTelemetry = ({ data = {} }) => {
  const defaultData = {
    isConnected: true,
    model: "DJI Smart Controller Enterprise",
    batteryLevel: 100,
    signalStrength: 90,
    ...data
  };

  return (
    <div className="telemetry-widget">
      <WidgetHeader 
        icon={<RCIcon />} 
        title="RC" 
        subtitle={defaultData.model} 
        isConnected={defaultData.isConnected} 
      />
      <div className="widget-body">
        <DataBlock 
          icon={<BatteryIcon level={defaultData.batteryLevel} />}
          label="Charged" 
          value={`${defaultData.batteryLevel}%`} 
        />
        <DataBlock 
          icon={<SignalIcon strength={defaultData.signalStrength} />}
          label="Signal Strength" 
          value={defaultData.signalStrength > 80 ? "Excellent" : defaultData.signalStrength > 60 ? "Good" : defaultData.signalStrength > 40 ? "Fair" : "Poor"} 
        />
      </div>
    </div>
  );
};

// UAVTelemetry Widget
export const UAVTelemetry = ({ data = {} }) => {
  const defaultData = {
    isConnected: true,
    model: "DJI Mavic 3T",
    batteryLevel: 100,
    flightTimeLeft: 160,
    satLocks: 15,
    airTraffic: "None",
    ...data
  };

  return (
    <div className="telemetry-widget">
      <WidgetHeader 
        icon={<DroneIcon />} 
        title="UAV" 
        subtitle={defaultData.model} 
        isConnected={defaultData.isConnected} 
      />
      <div className="widget-body">
        <DataBlock 
          icon={<BatteryIcon level={defaultData.batteryLevel} />}
          label="Charged" 
          value={`${defaultData.batteryLevel}%`} 
        />
        <DataBlock 
          icon={<ClockIcon />}
          label="Est. Flight Time Left" 
          value={`${defaultData.flightTimeLeft} mins`} 
        />
        <DataBlock 
          icon={<SatelliteIcon />}
          label="Sat Locks" 
          value={defaultData.satLocks} 
        />
        <DataBlock 
          icon={<AirspaceIcon />}
          label="Air Traffic" 
          value={defaultData.airTraffic} 
        />
      </div>
    </div>
  );
};

// New combined UAV and RC Telemetry Widget with enhanced telemetry information
export const UAVRCTelemetry = ({ uavData = {}, rcData = {} }) => {
  const [isUAVVisible, setIsUAVVisible] = useState(true);
  const [isRCVisible, setIsRCVisible] = useState(true);
  
  const defaultUavData = {
    isConnected: true,
    model: "DJI Mavic 3T",
    batteryLevel: 100,
    flightTimeLeft: 160,
    satLocks: 15,
    airTraffic: "None",
    flightMode: "Unknown",
    isFlying: false,
    altitude: 0,
    speed: 0,
    windSpeed: 0,
    windDirection: 0,
    deviceStatus: "Normal",
    ...uavData
  };

  const defaultRcData = {
    isConnected: true,
    model: "DJI Smart Controller Enterprise",
    batteryLevel: 100,
    signalStrength: 90,
    ...rcData
  };

  // Determine flight status text
  const flightStatus = defaultUavData.isFlying ? "In Flight" : "Grounded";
  
  // Format flight mode for display
  const formatFlightMode = (mode) => {
    if (!mode) return "Unknown";
    // Convert camelCase or snake_case to Title Case with spaces
    return mode
  };

  return (
    <div className="telemetry-widget combined-widget">
      <div className="combined-sections">
        <div className={`widget-section ${isUAVVisible ? '' : 'collapsed'}`}>
          <WidgetHeader 
            icon={<DroneIcon />} 
            title="UAV" 
            subtitle={defaultUavData.model} 
            isConnected={defaultUavData.isConnected}
            onToggleVisibility={() => setIsUAVVisible(!isUAVVisible)}
            isVisible={isUAVVisible}
          />
          {isUAVVisible && (
            <div className="widget-body">
              <div className="data-row">
                <DataBlock 
                  icon={<BatteryIcon level={defaultUavData.batteryLevel} />}
                  label="Battery" 
                  value={`${defaultUavData.batteryLevel}%`} 
                />
                <DataBlock 
                  icon={<FlightModeIcon />}
                  label="Flight Mode" 
                  value={formatFlightMode(defaultUavData.flightMode)} 
                />
              </div>
            </div>
          )}
        </div>

        <div className="divider"></div>

        <div className={`widget-section ${isRCVisible ? '' : 'collapsed'}`}>
          <WidgetHeader 
            icon={<RCIcon />} 
            title="RC" 
            subtitle={defaultRcData.model} 
            isConnected={defaultRcData.isConnected}
            onToggleVisibility={() => setIsRCVisible(!isRCVisible)}
            isVisible={isRCVisible}
          />
          {isRCVisible && (
            <div className="widget-body">
              <div className="data-row">
                <DataBlock 
                  icon={<BatteryIcon level={defaultRcData.batteryLevel} />}
                  label="Battery" 
                  value={`${defaultRcData.batteryLevel}%`} 
                />
                <DataBlock 
                  icon={<SignalIcon strength={defaultRcData.signalStrength} />}
                  label="Signal Strength" 
                  value={defaultRcData.signalStrength > 80 ? "Excellent" : defaultRcData.signalStrength > 60 ? "Good" : defaultRcData.signalStrength > 40 ? "Fair" : "Poor"} 
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced CoordinatesTelemetry Widget with more detailed location information
export const CoordinatesTelemetry = ({ data = {} }) => {
  const [coordFormat, setCoordFormat] = useState(0); // 0 = decimal, 1 = DMS
  const [isVisible, setIsVisible] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // console.log("CoordinatesTelemetry data:", data);
  const defaultData = {
    heading: 24,
    longitude: "-0.8777758",
    latitude: "51.4001780",
    verticalSpeed: 2,
    horizontalSpeed: 12,
    flightTime: "00:35:56",
    distanceFromHome: 500,
    altitude: 0,
    attitude: { pitch: 0, roll: 0, yaw: 0 },
    gpsSignal: 0,
    gpsCount: 0,
    isFlying: false,
    ...data
  };

  // Format GPS signal text
  const getGpsSignalText = (signal) => {
    if (signal >= 12) return "Strong";
    if (signal >= 10) return "Good";
    if (signal >= 6) return "Fair";
    if (signal >= 4) return "Weak";
    return "No Signal";
  };
  
  // Determine flight status text
  const flightStatus = data.isFlying ? "In Flight" : "Grounded";
  
  // Format heading for display
  const formatHeading = (heading) => {
    // Ensure heading is a number and within 0-360 range
    let numHeading = Number(heading) || 0;
    numHeading = ((numHeading % 360) + 360) % 360;
    
    // Format to two decimal places
    return numHeading.toFixed(2);
  };
  
  // Function to copy all telemetry data to clipboard
  const copyAllTelemetry = () => {
    const telemetryText = [
      `Heading: ${formatHeading(defaultData.heading)}°`,
      `Longitude: ${coordFormat === 0 ? defaultData.longitude : "0° 52' 40\" W"}`,
      `Latitude: ${coordFormat === 0 ? defaultData.latitude : "51° 24' 01\" N"}`,
      `Altitude: ${data.altitude}m`,
      `Status: ${flightStatus}`,
      `Vertical Speed: ${data.verticalSpeed}m/s`,
      `Horizontal Speed: ${data.horizontalSpeed}m/s`,
      `GPS Signal: ${getGpsSignalText(defaultData.gpsSignal)} (${defaultData.gpsCount} sats)`,
      `Time in Flight2: ${data.flightTime}`,
      `Distance from Home: ${data.distanceFromHome}m`
    ].join('\n');

    navigator.clipboard.writeText(telemetryText)
      .then(() => {
        console.log('All telemetry copied to clipboard');
        setCopySuccess(true);
        // Hide success message after 3 seconds
        setTimeout(() => {
          setCopySuccess(false);
        }, 3000);
      })
      .catch(err => {
        console.error('Error copying telemetry: ', err);
      });
  };

  return (
    <div className={`telemetry-widget ${isVisible ? '' : 'collapsed'}`}>
      <SimpleHeader 
        title="NAVIGATION" 
        rightElement={
          isVisible && (
            <div className="header-tools">
              <button 
                className={`copy-all-button ${copySuccess ? 'copied' : ''}`}
                onClick={copyAllTelemetry}
                title="Copy all telemetry data"
              >
                {copySuccess ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {copySuccess ? 'Copied' : 'Copy'}
              </button>
              <ToggleSwitch 
                options={['Decimal', 'DMS']} 
                activeIndex={coordFormat} 
                onChange={setCoordFormat} 
              />
            </div>
          )
        }
        onToggleVisibility={() => setIsVisible(!isVisible)}
        isVisible={isVisible}
      />
      <div className="widget-body coordinates no-padding">
        <div className="telemetry-section navigation-section">
          <div className="coordinates-grid">
            <div className="coordinates-row">
              <span className="coord-label">Heading:</span>
              <span className="coord-value">
                {formatHeading(defaultData.heading)}°
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Longitude:</span>
              <span className="coord-value">
                {coordFormat === 0 ? defaultData.longitude : "0° 52' 40\" W"}
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Latitude:</span>
              <span className="coord-value">
                {coordFormat === 0 ? defaultData.latitude : "51° 24' 01\" N"}
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Altitude:</span>
              <span className="coord-value">
                {data.altitude}m
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Status:</span>
              <span className="coord-value">
                {flightStatus}
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Ver. Speed:</span>
              <span className="coord-value">
                {data.verticalSpeed}m/s
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Hor. Speed:</span>
              <span className="coord-value">
                {data.horizontalSpeed}m/s
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">GPS Signal:</span>
              <span className="coord-value">
                {getGpsSignalText(defaultData.gpsSignal)} ({defaultData.gpsCount} sats)
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Time in Flight:</span>
              <span className="coord-value">
                {data.flightTime}
              </span>
            </div>
            <div className="coordinates-row">
              <span className="coord-label">Dist. from Home:</span>
              <span className="coord-value">
                {data.distanceFromHome}m
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Joystick Control
const JoystickControl = ({ size, position, angle, normalizedDistance, showJoystick, setShowJoystick, joystickRef, onMouseDown, onTouchStart }) => {
  // Toggle joystick visibility
  const toggleJoystick = () => {
    console.log("Toggle button clicked, current state:", showJoystick);
    setShowJoystick(prevState => !prevState);
  };
  
  return (
    <div className={`joystick-container ${showJoystick ? 'visible' : ''}`}>
      <button 
        className="joystick-toggle" 
        onClick={toggleJoystick}
        type="button"
        aria-label={showJoystick ? "Hide joystick control" : "Show joystick control"}
      >
        <JoystickIcon isVisible={showJoystick} />
      </button>
      
      {showJoystick && (
        <div 
          className="joystick" 
          ref={joystickRef}
          style={{ width: size, height: size }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <div className="joystick-base">
            {/* Crosshair */}
            <div className="joystick-crosshair-h"></div>
            <div className="joystick-crosshair-v"></div>
            
            {/* Direction arrow */}
            {normalizedDistance > 0.05 && (
              <div className="joystick-arrow" style={{
                width: `${arrowLength}px`,
                transform: `rotate(${angle}deg)`
              }}></div>
            )}
            
            {/* Handle */}
            <div 
              className="joystick-handle" 
              style={{
                transform: `translate(${position.x}px, ${position.y}px)`
              }}
            ></div>
          </div>
          
          {/* Directional info */}
          <div className="joystick-info">
            <div>Angle: {Math.round(angle)}°</div>
            <div>Distance: {Math.round(normalizedDistance * 100)}%</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoystickControl; 